import { useEffect, useState, useMemo } from 'react'
import {
  SparklesIcon,
  CpuChipIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ChartBarIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  BookOpenIcon,
  BeakerIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  FlagIcon,
  BoltIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import apiClient from '@/services/api'

// Icon mapping for node types
const iconMap: Record<string, React.ElementType> = {
  sparkles: SparklesIcon,
  code: CodeBracketIcon,
  document: DocumentTextIcon,
  chart: ChartBarIcon,
  table: TableCellsIcon,
  presentation: PresentationChartBarIcon,
  book: BookOpenIcon,
  beaker: BeakerIcon,
  'magnifying-glass': MagnifyingGlassIcon,
  'arrow-path': ArrowPathIcon,
  bolt: BoltIcon,
  flag: FlagIcon,
  cpu: CpuChipIcon,
}

interface ZiroNode {
  id: string
  name: string
  type: string
  description: string
  position: { x: number; y: number }
  icon: string
  specialist_type?: string
  status: string
}

interface ZiroEdge {
  source: string
  target: string
  label: string
  condition: string | null
  is_conditional: boolean
}

interface ZiroGraphData {
  nodes: ZiroNode[]
  edges: ZiroEdge[]
  node_groups: Record<string, string[]>
  layout: {
    direction: string
    layers: string[][]
  }
}

interface NodeStatus {
  [nodeId: string]: 'pending' | 'active' | 'completed' | 'revision' | 'error'
}

interface ZiroGraphDiagramProps {
  activeNodes?: NodeStatus
  currentPhase?: string
  showFullGraph?: boolean
  compact?: boolean
  customGraphData?: ZiroGraphData | null
  overriddenNodeIds?: Set<string>
}

export default function ZiroGraphDiagram({
  activeNodes = {},
  currentPhase = 'idle',
  showFullGraph = true,
  compact = false,
  customGraphData = null,
  overriddenNodeIds,
}: ZiroGraphDiagramProps) {
  const [graphData, setGraphData] = useState<ZiroGraphData | null>(customGraphData)
  const [loading, setLoading] = useState(!customGraphData)
  const [error, setError] = useState<string | null>(null)

  // Use custom data if provided, otherwise fetch from API
  useEffect(() => {
    if (customGraphData) {
      setGraphData(customGraphData)
      setLoading(false)
      return
    }
    const fetchGraph = async () => {
      try {
        const data = await apiClient.getZiroGraph()
        setGraphData(data)
      } catch (err: any) {
        setError(err.message || 'Failed to load graph')
      } finally {
        setLoading(false)
      }
    }
    fetchGraph()
  }, [customGraphData])

  // Get node status with fallback to activeNodes prop
  const getNodeStatus = (nodeId: string): 'pending' | 'active' | 'completed' | 'revision' | 'error' => {
    if (activeNodes[nodeId]) return activeNodes[nodeId]

    // Infer status from currentPhase
    if (currentPhase === 'idle' || currentPhase === 'starting') return 'pending'
    if (currentPhase === 'completed') return 'completed'
    if (currentPhase === 'analyzing' && nodeId === 'supervisor') return 'active'

    return 'pending'
  }

  // Get colors based on status
  const getNodeColors = (status: string) => {
    switch (status) {
      case 'active':
        return {
          bg: 'bg-blue-900/40',
          border: 'border-blue-500',
          text: 'text-blue-300',
          icon: 'text-blue-400',
          glow: 'shadow-lg shadow-blue-500/30',
          ring: 'ring-2 ring-blue-400 ring-opacity-50'
        }
      case 'completed':
        return {
          bg: 'bg-green-900/40',
          border: 'border-green-500',
          text: 'text-green-300',
          icon: 'text-green-400',
          glow: '',
          ring: ''
        }
      case 'revision':
        return {
          bg: 'bg-yellow-900/40',
          border: 'border-yellow-500',
          text: 'text-yellow-300',
          icon: 'text-yellow-400',
          glow: 'shadow-lg shadow-yellow-500/30',
          ring: 'ring-2 ring-yellow-400 ring-opacity-50'
        }
      case 'error':
        return {
          bg: 'bg-red-900/40',
          border: 'border-red-500',
          text: 'text-red-300',
          icon: 'text-red-400',
          glow: '',
          ring: ''
        }
      default:
        return {
          bg: 'bg-gray-800/60',
          border: 'border-gray-600',
          text: 'text-gray-400',
          icon: 'text-gray-500',
          glow: '',
          ring: ''
        }
    }
  }

  // Group nodes by layer for layout
  const nodesByLayer = useMemo(() => {
    if (!graphData) return []

    const layers: ZiroNode[][] = []
    const nodeMap = new Map(graphData.nodes.map(n => [n.id, n]))

    // Use the layout layers from the API
    graphData.layout.layers.forEach(layerIds => {
      const layerNodes = layerIds
        .map(id => nodeMap.get(id))
        .filter((n): n is ZiroNode => n !== undefined)
      if (layerNodes.length > 0) {
        layers.push(layerNodes)
      }
    })

    return layers
  }, [graphData])

  if (loading) {
    return (
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center justify-center py-8">
          <ArrowPathIcon className="w-6 h-6 text-gray-500 animate-spin mr-2" />
          <span className="text-gray-400">Loading Ziro graph...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-gray-900 rounded-lg p-6 border border-red-700">
        <div className="flex items-center text-red-400">
          <ExclamationCircleIcon className="w-5 h-5 mr-2" />
          {error}
        </div>
      </div>
    )
  }

  if (!graphData) return null

  return (
    <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <SparklesIcon className="w-5 h-5 mr-2 text-primary-400" />
          {(graphData as any)?.name || 'Ziro Intelligence Graph'}
        </h3>
        {currentPhase !== 'idle' && (
          <span className={clsx(
            'text-sm px-2 py-1 rounded',
            currentPhase === 'completed' ? 'bg-green-900/30 text-green-400' :
            currentPhase === 'error' ? 'bg-red-900/30 text-red-400' :
            'bg-blue-900/30 text-blue-400'
          )}>
            {currentPhase === 'completed' ? 'Completed' :
             currentPhase === 'error' ? 'Error' :
             'Running...'}
          </span>
        )}
      </div>

      {/* Graph Visualization */}
      <div className="relative overflow-x-auto">
        <div className="flex flex-col items-center space-y-4 min-w-[800px]">
          {nodesByLayer.map((layer, layerIdx) => (
            <div key={layerIdx} className="w-full">
              {/* Connection lines from previous layer */}
              {layerIdx > 0 && (
                <div className="flex justify-center mb-2">
                  <div className="w-0.5 h-6 bg-gray-600" />
                </div>
              )}

              {/* Layer nodes */}
              <div className={clsx(
                'flex justify-center gap-3 flex-wrap',
                layer.length > 4 ? 'gap-2' : 'gap-4'
              )}>
                {layer.map(node => {
                  const status = getNodeStatus(node.id)
                  const colors = getNodeColors(status)
                  const Icon = iconMap[node.icon] || CpuChipIcon
                  const isOverridden = overriddenNodeIds?.has(node.id)

                  return (
                    <div
                      key={node.id}
                      data-node-id={node.id}
                      className={clsx(
                        'relative flex flex-col items-center p-3 rounded-lg border-2 transition-all duration-300 cursor-pointer hover:brightness-110',
                        compact ? 'min-w-[100px] max-w-[120px]' : 'min-w-[140px] max-w-[180px]',
                        isOverridden ? 'bg-purple-900/40 border-purple-500 ring-2 ring-purple-400 ring-opacity-60 shadow-lg shadow-purple-500/30' : clsx(colors.bg, colors.border, colors.glow, colors.ring)
                      )}
                      title={node.description}
                    >
                      {isOverridden && (
                        <span className="absolute -top-2 -right-2 text-[8px] px-1 py-0.5 rounded bg-purple-500 text-white font-bold z-10">modified</span>
                      )}
                      {/* Status indicator */}
                      <div className="flex items-center mb-1">
                        <Icon className={clsx('w-5 h-5', isOverridden ? 'text-purple-400' : colors.icon)} />
                        {status === 'active' && (
                          <ArrowPathIcon className="w-3 h-3 ml-1 animate-spin text-blue-400" />
                        )}
                        {status === 'completed' && (
                          <CheckCircleIcon className="w-3 h-3 ml-1 text-green-400" />
                        )}
                        {status === 'revision' && (
                          <ArrowPathIcon className="w-3 h-3 ml-1 text-yellow-400" />
                        )}
                        {status === 'error' && (
                          <ExclamationCircleIcon className="w-3 h-3 ml-1 text-red-400" />
                        )}
                      </div>

                      {/* Node name */}
                      <span className={clsx(
                        'text-xs font-medium text-center leading-tight',
                        colors.text
                      )}>
                        {node.name}
                      </span>

                      {/* Node type badge */}
                      {!compact && (
                        <span className="mt-1 text-[10px] text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded">
                          {node.type}
                        </span>
                      )}

                      {/* Conditional edge indicator */}
                      {graphData.edges.some(e =>
                        e.source === node.id && e.is_conditional
                      ) && (
                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                          <BoltIcon className="w-3 h-3 text-purple-400" />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-4 mt-6 pt-4 border-t border-gray-700">
        <div className="flex items-center text-xs text-gray-500">
          <div className="w-3 h-3 rounded-full bg-gray-600 mr-1.5" />
          Pending
        </div>
        <div className="flex items-center text-xs text-blue-400">
          <div className="w-3 h-3 rounded-full bg-blue-500 mr-1.5" />
          Active
        </div>
        <div className="flex items-center text-xs text-green-400">
          <div className="w-3 h-3 rounded-full bg-green-500 mr-1.5" />
          Completed
        </div>
        <div className="flex items-center text-xs text-yellow-400">
          <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1.5" />
          Revision
        </div>
        <div className="flex items-center text-xs text-purple-400">
          <BoltIcon className="w-3 h-3 mr-1" />
          Conditional
        </div>
      </div>

      {/* Graph metadata */}
      {showFullGraph && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-500">
            <div>
              <span className="text-gray-400">Nodes:</span> {graphData.nodes.length}
            </div>
            <div>
              <span className="text-gray-400">Edges:</span> {graphData.edges.length}
            </div>
            <div>
              <span className="text-gray-400">Specialists:</span> {graphData.node_groups.specialists?.length || 0}
            </div>
            <div>
              <span className="text-gray-400">Reviews:</span> {graphData.node_groups.reviews?.length || 0}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
